import React, { useCallback, useEffect, useState } from 'react';
import Card from '../../../../shared/components/Card';
import LoadingSpinner from '../../../../shared/components/LoadingSpinner';
import userService, {
  ReferentFunctionDefinition,
  ReferentTypeDefinition,
} from '../../../../shared/services/userService';

const TYPE_KEY_MAX = 100;

/**
 * Gestion des types puis des fonctions référent.
 * L’assignation à un utilisateur se fait depuis l’onglet « Gérer les rôles ».
 */
const ReferentDefinitionsContent: React.FC = () => {
  const [types, setTypes] = useState<ReferentTypeDefinition[]>([]);
  const [definitions, setDefinitions] = useState<ReferentFunctionDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /* Nouveau type */
  const [newTypeKey, setNewTypeKey] = useState('');
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeDesc, setNewTypeDesc] = useState('');

  /* Nouvelle fonction */
  const [newKey, setNewKey] = useState('');
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newFunctionTypeId, setNewFunctionTypeId] = useState<number | ''>('');

  const [editingType, setEditingType] = useState<ReferentTypeDefinition | null>(null);
  const [editTypeKey, setEditTypeKey] = useState('');
  const [editTypeName, setEditTypeName] = useState('');
  const [editTypeDesc, setEditTypeDesc] = useState('');
  const [editTypeActive, setEditTypeActive] = useState(true);

  const [editing, setEditing] = useState<ReferentFunctionDefinition | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editFunctionTypeId, setEditFunctionTypeId] = useState<number | ''>('');
  const [editActive, setEditActive] = useState(true);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const [resTypes, resDefs] = await Promise.all([
        userService.getReferentTypes(),
        userService.getReferentDefinitions(),
      ]);
      if (resTypes.success) setTypes(resTypes.data);
      if (resDefs.success) setDefinitions(resDefs.data);
    } catch (e: any) {
      setError(e.message || 'Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeTypes = types.filter((t) => t.is_active);

  const handleCreateType = async () => {
    setSaving(true);
    setError(null);
    try {
      await userService.createReferentType({
        type_key: newTypeKey.trim(),
        name: newTypeName.trim(),
        description: newTypeDesc.trim() || undefined,
      });
      setNewTypeKey('');
      setNewTypeName('');
      setNewTypeDesc('');
      await load();
    } catch (e: any) {
      setError(e.message || 'Erreur à la création du type');
    } finally {
      setSaving(false);
    }
  };

  const openEditType = (t: ReferentTypeDefinition) => {
    setEditingType(t);
    setEditTypeKey(t.type_key);
    setEditTypeName(t.name);
    setEditTypeDesc(t.description || '');
    setEditTypeActive(!!t.is_active);
  };

  const handleSaveEditType = async () => {
    if (!editingType) return;
    setSaving(true);
    setError(null);
    try {
      await userService.updateReferentType(editingType.id, {
        type_key: editTypeKey.trim(),
        name: editTypeName.trim(),
        description: editTypeDesc.trim() || undefined,
        is_active: editTypeActive,
      });
      setEditingType(null);
      await load();
    } catch (e: any) {
      setError(e.message || 'Erreur à la mise à jour du type');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateType = async (t: ReferentTypeDefinition) => {
    if (
      !window.confirm(
        `Désactiver le type « ${t.name} » ? Impossible s’il est encore utilisé par des fonctions actives.`
      )
    ) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await userService.deactivateReferentType(t.id);
      await load();
    } catch (e: any) {
      setError(e.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (newFunctionTypeId === '') return;
    setSaving(true);
    setError(null);
    try {
      await userService.createReferentDefinition({
        function_key: newKey.trim(),
        name: newName.trim(),
        description: newDesc.trim() || undefined,
        referent_type_id: newFunctionTypeId,
      });
      setNewKey('');
      setNewName('');
      setNewDesc('');
      setNewFunctionTypeId('');
      await load();
    } catch (e: any) {
      setError(e.message || 'Erreur à la création');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (d: ReferentFunctionDefinition) => {
    setEditing(d);
    setEditName(d.name);
    setEditDesc(d.description || '');
    setEditFunctionTypeId(d.referent_type_id);
    setEditActive(!!d.is_active);
  };

  const handleSaveEdit = async () => {
    if (!editing || editFunctionTypeId === '') return;
    setSaving(true);
    setError(null);
    try {
      await userService.updateReferentDefinition(editing.id, {
        name: editName.trim(),
        description: editDesc.trim(),
        referent_type_id: editFunctionTypeId,
        is_active: editActive,
      });
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e.message || 'Erreur à la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (d: ReferentFunctionDefinition) => {
    if (!window.confirm(`Désactiver la fonction « ${d.name} » ? Elle ne sera plus proposée aux nouvelles assignations.`)) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await userService.deactivateReferentDefinition(d.id);
      await load();
    } catch (e: any) {
      setError(e.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-200 text-sm">
        <p className="font-semibold text-white mb-1">À quoi sert cet onglet ?</p>
        <p>
          Créez d’abord un <strong>type</strong> de référent, puis les <strong>fonctions</strong> associées à ce type.
          Chaque fonction appartient à un type : cela sert à organiser les rôles et les listes lors des assignations.
          Pour donner une fonction à un membre : onglet <strong>Gérer les rôles</strong>, puis l’icône référent.
        </p>
        <p className="mt-2 text-gray-400 text-xs">
          En cas de message d’erreur ou de fonctionnalité indisponible, contactez l’équipe technique du site.
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">{error}</div>
      )}

      {/* ——— Types ——— */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-white mb-4">Types référent</h3>
        <div className="grid md:grid-cols-2 gap-4 mb-6 pb-6 border-b border-gray-700">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Clé unique du type</label>
            <input
              value={newTypeKey}
              onChange={(e) => setNewTypeKey(e.target.value.slice(0, TYPE_KEY_MAX))}
              placeholder="ex. CM, Formation"
              maxLength={TYPE_KEY_MAX}
              className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Nom affiché</label>
            <input
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder="ex. Community Manager"
              className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-300 mb-1">Description (optionnel)</label>
            <textarea
              value={newTypeDesc}
              onChange={(e) => setNewTypeDesc(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={handleCreateType}
          disabled={saving || !newTypeKey.trim() || !newTypeName.trim()}
          className="mb-6 px-6 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg font-semibold"
        >
          {saving ? 'Enregistrement…' : 'Créer le type'}
        </button>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : types.length === 0 ? (
          <p className="text-gray-400">Aucun type. Créez-en un ci-dessus (ou importez le schéma SQL).</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700 text-left text-gray-400">
                  <th className="py-2 pr-4">Clé</th>
                  <th className="py-2 pr-4">Nom</th>
                  <th className="py-2 pr-4">Statut</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {types.map((t) => (
                  <tr key={t.id} className="border-b border-gray-800 text-gray-300">
                    <td className="py-3 pr-4 font-mono text-xs text-white">{t.type_key}</td>
                    <td className="py-3 pr-4">{t.name}</td>
                    <td className="py-3 pr-4">
                      {t.is_active ? <span className="text-green-400">Actif</span> : <span className="text-gray-500">Inactif</span>}
                    </td>
                    <td className="py-3 text-right space-x-2">
                      <button
                        type="button"
                        onClick={() => openEditType(t)}
                        className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white"
                      >
                        Modifier
                      </button>
                      {t.is_active && (
                        <button
                          type="button"
                          onClick={() => handleDeactivateType(t)}
                          disabled={saving}
                          className="px-3 py-1 bg-red-600/80 hover:bg-red-600 rounded text-white disabled:opacity-50"
                        >
                          Désactiver
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ——— Fonctions ——— */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-white mb-4">Fonctions référent</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Identifiant unique (clé fonction)</label>
            <input
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="ex. Référent Formation"
              className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
            />
            <p className="text-xs text-gray-500 mt-1">Ne pas changer après usage en base.</p>
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Nom affiché</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="ex. Référent Formation"
              className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-300 mb-1">Type (obligatoire — créé ci-dessus)</label>
            <select
              value={newFunctionTypeId === '' ? '' : String(newFunctionTypeId)}
              onChange={(e) => setNewFunctionTypeId(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
            >
              <option value="">— Choisir un type —</option>
              {activeTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.type_key})
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-300 mb-1">Description (optionnel)</label>
            <textarea
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={handleCreate}
          disabled={saving || !newKey.trim() || !newName.trim() || newFunctionTypeId === '' || activeTypes.length === 0}
          className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-semibold"
        >
          {saving ? 'Enregistrement…' : 'Créer la fonction'}
        </button>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-bold text-white mb-4">Liste des fonctions</h3>
        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : definitions.length === 0 ? (
          <p className="text-gray-400">Aucune définition.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700 text-left text-gray-400">
                  <th className="py-2 pr-4">Clé</th>
                  <th className="py-2 pr-4">Nom</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Statut</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {definitions.map((d) => (
                  <tr key={d.id} className="border-b border-gray-800 text-gray-300">
                    <td className="py-3 pr-4 font-mono text-xs text-white">{d.function_key}</td>
                    <td className="py-3 pr-4">{d.name}</td>
                    <td className="py-3 pr-4">
                      <span className="text-gray-200">{d.type_name}</span>
                      <span className="text-gray-500 text-xs ml-1">({d.type_key})</span>
                    </td>
                    <td className="py-3 pr-4">
                      {d.is_active ? (
                        <span className="text-green-400">Actif</span>
                      ) : (
                        <span className="text-gray-500">Inactif</span>
                      )}
                    </td>
                    <td className="py-3 text-right space-x-2">
                      <button
                        type="button"
                        onClick={() => openEdit(d)}
                        className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white"
                      >
                        Modifier
                      </button>
                      {d.is_active && (
                        <button
                          type="button"
                          onClick={() => handleDeactivate(d)}
                          disabled={saving}
                          className="px-3 py-1 bg-red-600/80 hover:bg-red-600 rounded text-white disabled:opacity-50"
                        >
                          Désactiver
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editingType && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
          <Card className="max-w-lg w-full p-6">
            <h3 className="text-xl font-bold text-white mb-2">Modifier le type — {editingType.type_key}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Clé du type</label>
                <input
                  value={editTypeKey}
                  onChange={(e) => setEditTypeKey(e.target.value.slice(0, TYPE_KEY_MAX))}
                  maxLength={TYPE_KEY_MAX}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Nom affiché</label>
                <input
                  value={editTypeName}
                  onChange={(e) => setEditTypeName(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Description</label>
                <textarea
                  value={editTypeDesc}
                  onChange={(e) => setEditTypeDesc(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
                />
              </div>
              <label className="flex items-center gap-2 text-gray-300 text-sm">
                <input type="checkbox" checked={editTypeActive} onChange={(e) => setEditTypeActive(e.target.checked)} />
                Actif
              </label>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                type="button"
                onClick={handleSaveEditType}
                disabled={saving || !editTypeKey.trim() || !editTypeName.trim()}
                className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-white font-semibold disabled:opacity-50"
              >
                Enregistrer
              </button>
              <button
                type="button"
                onClick={() => setEditingType(null)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                Annuler
              </button>
            </div>
          </Card>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
          <Card className="max-w-lg w-full p-6">
            <h3 className="text-xl font-bold text-white mb-2">Modifier — {editing.function_key}</h3>
            <p className="text-gray-500 text-xs mb-4">La clé technique n’est pas modifiable.</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Nom affiché</label>
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Type</label>
                <select
                  value={editFunctionTypeId === '' ? '' : String(editFunctionTypeId)}
                  onChange={(e) => setEditFunctionTypeId(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
                >
                  <option value="">— Choisir —</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id} disabled={!t.is_active && t.id !== editing.referent_type_id}>
                      {t.name} ({t.type_key}){!t.is_active ? ' — inactif' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Description</label>
                <textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
                />
              </div>
              <label className="flex items-center gap-2 text-gray-300 text-sm">
                <input type="checkbox" checked={editActive} onChange={(e) => setEditActive(e.target.checked)} />
                Actif (proposé dans les listes)
              </label>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={saving || !editName.trim() || editFunctionTypeId === ''}
                className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-semibold disabled:opacity-50"
              >
                Enregistrer
              </button>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                Annuler
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ReferentDefinitionsContent;
